import React, { useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, X, Loader2, GripVertical } from 'lucide-react';

interface MediaUploadSectionProps {
  images: string[];
  uploadingImages: boolean;
  maxImages?: number;
  onImagesChange: (images: string[]) => void;
  onUploadImages: (files: FileList) => Promise<void>;
}

const MediaUploadSection: React.FC<MediaUploadSectionProps> = ({
  images,
  uploadingImages,
  maxImages = 5,
  onImagesChange,
  onUploadImages,
}) => {
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (images.length + files.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images`);
      return;
    }

    await onUploadImages(files);
    // Clear the input
    e.target.value = '';
  }, [images.length, maxImages, onUploadImages]);

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const dragIndexRef = useRef<number | null>(null);

  const handleThumbDragStart = (index: number) => {
    dragIndexRef.current = index;
  };

  const handleThumbDrop = (index: number) => {
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from === null || from === index) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);
    onImagesChange(next);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    if (images.length + files.length > maxImages) {
      alert(`You can only upload up to ${maxImages} images`);
      return;
    }

    await onUploadImages(files);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Photos & Videos</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Upload Images (Max {maxImages})</Label>
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              id="image-upload"
              disabled={uploadingImages || images.length >= maxImages}
            />
            <label
              htmlFor="image-upload"
              className={`cursor-pointer ${
                uploadingImages || images.length >= maxImages ? 'cursor-not-allowed opacity-50' : ''
              }`}
            >
              <div className="flex flex-col items-center">
                {uploadingImages ? (
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400 mb-2" />
                ) : (
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                )}
                <p className="text-sm text-gray-600">
                  {uploadingImages
                    ? 'Uploading...'
                    : images.length >= maxImages
                    ? `Maximum ${maxImages} images reached`
                    : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG, GIF up to 10MB each
                </p>
              </div>
            </label>
          </div>
        </div>

        {images.length > 0 && (
          <div>
            <Label>Uploaded Images ({images.length}/{maxImages})</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
              {images.map((image, index) => (
                <div
                  key={index}
                  className="relative group"
                  draggable
                  onDragStart={() => handleThumbDragStart(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleThumbDrop(index)}
                  title="Drag to reorder"
                >
                  <img
                    src={image}
                    alt={`Display image ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  {/* Visual drag handle */}
                  <button
                    type="button"
                    aria-label="Drag to reorder"
                    className="absolute top-2 left-2 bg-black/50 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                    draggable
                    onDragStart={() => handleThumbDragStart(index)}
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemoveImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  {index === 0 && (
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                      Main Photo
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MediaUploadSection;